import { Skeleton } from 'antd'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Callout } from 'sutra/components/Callout/Callout'

import { buildAlertChartV2 } from 'features/alerts/features/create-alert/components/alert-chart-preview/alert-chart-v2-adapter'
import { chartFactory } from 'features/charts-v2/chart-factory'
import type { MetricType, TimeBucketOption } from 'features/charts-v2/types'
import type { ChartProps } from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type { DashboardChartResponse, LineChart } from 'features/charts/types'

type Props = {
  alert_type: string
  alert_scope: string
  trigger_type: string
  time_interval: string
  compare_value: number
  reviews_filter: number
  isFormValid: boolean
  chartData: unknown
  chartIsLoading: boolean
}

/** Maps the alert_type string to the v2 metric id used to select the Y-domain branch. */
const ALERT_TYPE_TO_METRIC: Record<string, MetricType> = {
  'Star Rating': 'rating',
  Sentiment: 'sentiment',
  Volume: 'record_count',
}

const TIME_INTERVAL_TO_BUCKET: Record<string, TimeBucketOption> = {
  Week: 'week',
  Month: 'month',
  Quarter: 'quarter',
}

export const AlertChartPreview: React.FC<Props> = ({
  alert_type,
  alert_scope,
  time_interval,
  chartData: previewChart,
  chartIsLoading: isLoading,
}) => {
  const previewChartResponse = previewChart as
    | DashboardChartResponse
    | undefined
  const currentChart = Array.isArray(previewChartResponse?.charts)
    ? previewChartResponse.charts[0]
    : undefined

  // Charts-v2 path: memoize the expensive series-transform so it only
  // re-runs when chart data or display params actually change, not on
  // every parent render.
  const v2Strategy = useMemo(
    () => chartFactory.getStrategy(ChartTypeV2.LINE),
    [],
  )
  const timeBucket: TimeBucketOption =
    TIME_INTERVAL_TO_BUCKET[time_interval] ?? 'month'
  const metric: MetricType = ALERT_TYPE_TO_METRIC[alert_type] ?? 'record_count'

  const v2Rendered = useMemo(() => {
    if (!currentChart || !v2Strategy) return null
    const { chart, referenceBounds } = buildAlertChartV2(
      currentChart as LineChart,
      alert_scope,
    )
    const chartProps: ChartProps = {
      data: chart,
      selectedMetrics: [metric],
      timeBucket,
      referenceBounds,
      showXAxisLabel: true,
      showYAxisLabel: false,
      showDatapointLabels: true,
      showSentimentZones: false,
      showRatingZones: false,
    }
    const transformed = v2Strategy.transform(chartProps)
    return v2Strategy.render({ ...chartProps, transformedData: transformed })
  }, [currentChart, v2Strategy, alert_scope, metric, timeBucket])

  // Trigger-count: count periods where the metric crosses the backend-drawn bound line.
  const triggerInfo = useMemo(() => {
    if (!currentChart) return null
    const { chart, referenceBounds } = buildAlertChartV2(
      currentChart as LineChart,
      alert_scope,
    )
    if (!referenceBounds) return null
    const { lower, upper } = referenceBounds
    let count = 0
    chart.data.forEach((point, i) => {
      const value = point.values[0]
      if (value == null) return
      const lo = lower?.[i]
      const hi = upper?.[i]
      if ((hi != null && value > hi) || (lo != null && value < lo)) count++
    })
    return { count, periodCount: chart.data.length }
  }, [currentChart, alert_scope])

  const periodUnit =
    time_interval === 'Week'
      ? 'weeks'
      : time_interval === 'Quarter'
        ? 'quarters'
        : 'months'

  if (isLoading)
    return (
      <LoadingContainer>
        <SkeletonTitle active size="small" />
        <SkeletonChartBlock active block />
      </LoadingContainer>
    )

  if (!previewChart || !currentChart) return <div></div>

  const chartEl = <Container>{v2Rendered}</Container>

  return (
    <Root>
      {chartEl}
      {triggerInfo && (
        <CalloutWrapper>
          <Callout variant="info">
            This alert would have triggered {triggerInfo.count}{' '}
            {triggerInfo.count === 1 ? 'time' : 'times'} in the last{' '}
            {triggerInfo.periodCount} {periodUnit}.
          </Callout>
        </CalloutWrapper>
      )}
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
`

const Container = styled.div`
  flex: 0 0 300px;
  height: 300px;
  max-height: 300px;
  min-height: 0;
  overflow: hidden;

  /* Keep the chart laid out within the 300px box instead of growing past it,
     so it can't paint over the Callout below. */
  & > div {
    height: 100%;
    max-height: 100%;
    min-height: 0;
  }
`

const LoadingContainer = styled.div`
  height: 300px;
  max-height: 300px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SkeletonTitle = styled(Skeleton.Input)`
  width: 180px !important;
`

const SkeletonChartBlock = styled(Skeleton.Input)`
  flex: 1;
  min-height: 0;

  /* antd sizes Skeleton.Input with a fixed inline-block height by default;
     stretch both the element wrapper and inner node to fill the flex row. */
  &.ant-skeleton.ant-skeleton-element,
  & .ant-skeleton-input {
    width: 100% !important;
    height: 100% !important;
  }
`

const CalloutWrapper = styled.div`
  margin-top: 12px;
`
